import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';
import TutorialAssignmentModal from '../components/TutorialAssignmentModal';
import { 
  BookOpen, 
  Clock, 
  Search,
  PlayCircle,
  CheckCircle,
  X,
  Users
} from 'lucide-react';

interface Tutorial {
  id: string;
  title: string;
  description: string;
  type: 'guide' | 'course' | 'presentation';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  category: string;
  product?: string | null;
  authorId: string;
  isPublished: boolean;
  language?: string;
  status?: string;
  createdAt: string;
  updatedAt: string;
}

interface Category {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  isActive: boolean;
  sortOrder: number;
}

const CourseCatalog: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { } = useLanguage();
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<any | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [enrolledIds, setEnrolledIds] = useState<string[]>([]);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [courseToEnroll, setCourseToEnroll] = useState<string | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [tutorialToAssign, setTutorialToAssign] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    fetchTutorials();
    fetchCategories();
    fetchEnrollments();
  }, []);

  // Refetch tutorials when language filter changes
  useEffect(() => {
    if (selectedLanguage !== 'all') {
      fetchTutorials(selectedLanguage);
    } else {
      fetchTutorials('en'); // Default to English when "All Languages" is selected
    }
  }, [selectedLanguage]);

  const fetchTutorials = async (language: string = 'en') => {
    try {
      setLoading(true);
      const response = await apiService.getTutorials(1, 100, language); // Get more tutorials for catalog view
      setTutorials(response.tutorials || []);
    } catch (err) {
      console.error('Tutorials fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tutorials');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.getCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (err) {
      console.error('Categories fetch error:', err);
    }
  };

  const enrollInCourse = async (tutorialId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      await apiService.enrollInCourse(tutorialId);

      // Show success toast
      const enrolled = tutorials.find(t => t.id === tutorialId);
      toast.success(`Enrolled in ${enrolled?.title ? '"' + enrolled.title + '"' : 'course'}!`);
      setEnrolledIds(prev => Array.from(new Set([...prev, tutorialId])));
    } catch (err: any) {
      console.error('Enrollment error:', err);
      
      if (err?.response?.data?.error?.toLowerCase().includes('already enrolled')) {
        // Mark as enrolled without error UI
        setEnrolledIds(prev => Array.from(new Set([...prev, tutorialId])));
        toast('Already enrolled', { icon: 'ℹ️' });
        return;
      }
      
      setError(err?.response?.data?.error || err.message || 'Failed to enroll in course');
    }
  };

  const fetchEnrollments = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const response = await apiService.getEnrollments();
      if (response.success && response.data) {
        setEnrolledIds((response.data as any)?.tutorialIds || []);
      }
    } catch (e) {
      // non-fatal
    }
  };

  const handleAssignTutorial = (tutorialId: string, tutorialTitle: string) => {
    setTutorialToAssign({ id: tutorialId, title: tutorialTitle });
    setShowAssignmentModal(true);
  };

  const handleAssignmentComplete = async (assignments: { userId: string; dueDate?: string }[]) => {
    if (!tutorialToAssign) return;

    try {
      // Create assignments for each selected user
      for (const assignment of assignments) {
        await apiService.makeRequest('/user-management/assignments', {
          method: 'POST',
          body: JSON.stringify({
            userId: assignment.userId,
            tutorialId: tutorialToAssign.id,
            dueDate: assignment.dueDate
          })
        });
      }

      toast.success(`Tutorial assigned to ${assignments.length} user${assignments.length !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error('Failed to assign tutorial');
    }
  };

  const openPreview = async (id: string) => {
    try {
      setPreviewId(id);
      setLoadingPreview(true);
      const response = await apiService.getTutorialOverview(id);
      if (response.success && response.data) {
        setPreviewData(response.data);
      } else {
        throw new Error('Failed to load overview');
      }
    } catch (e) {
      console.error('Preview load error:', e);
      toast.error('Failed to load course overview');
      setPreviewId(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner': return '🟢';
      case 'intermediate': return '🟡';
      case 'advanced': return '🔴';
      default: return '⚪';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'guide': return 'text-blue-600 bg-blue-100';
      case 'course': return 'text-green-600 bg-green-100';
      case 'presentation': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'guide': return '📖';
      case 'course': return '🎓';
      case 'presentation': return '📊';
      default: return '📄';
    }
  };

  const filteredTutorials = tutorials.filter(tutorial => {
    const matchesSearch = tutorial.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tutorial.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tutorial.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'all' || tutorial.difficulty === selectedDifficulty;
    const matchesType = selectedType === 'all' || tutorial.type === selectedType;
    // Language filtering is now handled by the API call
    const matchesProduct = selectedProduct === 'all' || tutorial.product === selectedProduct;
    const matchesStatus = selectedStatus === 'all' || 
      (selectedStatus === 'published' && tutorial.isPublished) ||
      (selectedStatus === 'draft' && !tutorial.isPublished) ||
      (selectedStatus === 'translating' && tutorial.status === 'translating');
    
    // Time range filter
    let matchesTimeRange = true;
    if (selectedTimeRange !== 'all') {
      const now = new Date();
      const tutorialDate = new Date(tutorial.createdAt);
      const daysDiff = Math.floor((now.getTime() - tutorialDate.getTime()) / (1000 * 60 * 60 * 24));
      
      switch (selectedTimeRange) {
        case 'today':
          matchesTimeRange = daysDiff === 0;
          break;
        case 'week':
          matchesTimeRange = daysDiff <= 7;
          break;
        case 'month':
          matchesTimeRange = daysDiff <= 30;
          break;
        case 'year':
          matchesTimeRange = daysDiff <= 365;
          break;
      }
    }
    
    return matchesSearch && matchesCategory && matchesDifficulty && matchesType && 
           matchesProduct && matchesStatus && matchesTimeRange;
  });

  const sortedTutorials = [...filteredTutorials].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'oldest':
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'title':
        return a.title.localeCompare(b.title);
      case 'difficulty':
        const difficultyOrder = { beginner: 1, intermediate: 2, advanced: 3 };
        return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading course catalog...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-red-600">{error}</p>
          <button 
            onClick={() => {
              setError(null);
              fetchTutorials();
            }}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Course Catalog</h1>
          <p className="text-gray-600 mt-2">
            Discover and enroll in courses to enhance your skills
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Difficulty Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            {/* Content Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="guide">Guides</option>
                <option value="course">Courses</option>
                <option value="presentation">Presentations</option>
              </select>
            </div>

            {/* Language Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Languages</option>
                <option value="en">English</option>
                <option value="es">Spanish</option>
              </select>
            </div>

            {/* Product Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Product</label>
              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Products</option>
                {Array.from(new Set(tutorials.map(t => t.product).filter(Boolean))).map(product => (
                  <option key={product} value={product || ''}>{product}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="translating">Translating</option>
              </select>
            </div>

            {/* Time Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Created</label>
              <select
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="title">Title A-Z</option>
                <option value="difficulty">Difficulty</option>
              </select>
            </div>
          </div>
          
          {/* Clear Filters Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
                setSelectedDifficulty('all');
                setSelectedType('all');
                setSelectedLanguage('all');
                setSelectedProduct('all');
                setSelectedStatus('all');
                setSelectedTimeRange('all');
                setSortBy('newest');
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing {sortedTutorials.length} of {tutorials.length} courses
          </p>
        </div>

        {/* Course Grid */}
        {sortedTutorials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedTutorials.map((tutorial) => (
              <div key={tutorial.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium mr-2 ${getTypeColor(tutorial.type)}`}>
                          {getTypeIcon(tutorial.type)} {tutorial.type}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(tutorial.difficulty)}`}>
                          {getDifficultyIcon(tutorial.difficulty)} {tutorial.difficulty}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                        {tutorial.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">{tutorial.category}</p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {tutorial.description}
                  </p>

                  {/* Meta Info */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {tutorial.estimatedTime}
                    </div>
                    <div className="flex items-center">
                      <BookOpen className="h-4 w-4 mr-1" />
                      Course
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    {enrolledIds.includes(tutorial.id) ? (
                      <button
                        disabled
                        className="flex-1 bg-green-100 text-green-700 py-2 px-4 rounded-lg cursor-default flex items-center justify-center"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Enrolled
                      </button>
                    ) : (
                      <button
                        onClick={() => enrollInCourse(tutorial.id)}
                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                      >
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Enroll
                      </button>
                    )}
                    <button
                      onClick={() => openPreview(tutorial.id)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Preview
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your search criteria or browse all categories
            </p>
            <button 
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
                setSelectedDifficulty('all');
              }}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
      {/* Preview Modal */}
      {previewId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Course Overview</h3>
              <button onClick={() => { setPreviewId(null); setPreviewData(null); }} className="p-2 rounded hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6">
              {loadingPreview ? (
                <div className="text-center text-gray-600">Loading...</div>
              ) : previewData ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">{previewData.title}</h4>
                    <p className="text-gray-600 mt-1">{previewData.description}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center space-x-2"><span className="font-medium">Type:</span><span className="capitalize">{previewData.type}</span></div>
                    <div className="flex items-center space-x-2"><span className="font-medium">Difficulty:</span><span className="capitalize">{previewData.difficulty}</span></div>
                    <div className="flex items-center space-x-2"><Clock className="w-4 h-4" /><span>{previewData.estimatedTime}</span></div>
                    <div className="flex items-center space-x-2"><span className="font-medium">Category:</span><span>{previewData.category}</span></div>
                    <div className="flex items-center space-x-2"><span className="font-medium">Steps:</span><span>{previewData.stepsCount}</span></div>
                    <div className="flex items-center space-x-2"><span className="font-medium">Enrollments:</span><span>{previewData.enrollments}</span></div>
                    <div className="flex items-center space-x-2"><span className="font-medium">Assignments:</span><span>{previewData.assignments}</span></div>
                    <div className="flex items-center space-x-2"><span className="font-medium">Completions:</span><span>{previewData.completions}</span></div>
                    <div className="flex items-center space-x-2 col-span-2"><span className="font-medium">Created:</span><span>{new Date(previewData.createdAt).toLocaleString()}</span></div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-600">No data</div>
              )}
            </div>
            <div className="p-4 border-t flex items-center justify-end space-x-2">
              <button onClick={() => navigate(`/tutorials/${previewId}`)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Take Course</button>
              <button onClick={() => { if (previewId) { setCourseToEnroll(previewId); setShowEnrollmentModal(true); } }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Enroll</button>
              {/* Only non-learners can assign */}
              {user?.roles?.some(r => r === 'admin' || r === 'manager' || r === 'instructor') && (
                <button 
                  onClick={() => handleAssignTutorial(previewId!, previewData?.title || 'Unknown Tutorial')} 
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                >
                  <Users className="w-4 h-4" />
                  <span>Assign to Users</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enrollment Confirmation Modal */}
      {showEnrollmentModal && courseToEnroll && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Enroll in Course</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                You're about to enroll in <strong>{tutorials.find(t => t.id === courseToEnroll)?.title}</strong>. 
                Would you like to start the course right away?
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={async () => {
                    await enrollInCourse(courseToEnroll);
                    setShowEnrollmentModal(false);
                    setCourseToEnroll(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Just Enroll
                </button>
                <button
                  onClick={async () => {
                    await enrollInCourse(courseToEnroll);
                    setShowEnrollmentModal(false);
                    setCourseToEnroll(null);
                    navigate(`/tutorials/${courseToEnroll}`);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Enroll & Start Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tutorial Assignment Modal */}
      {showAssignmentModal && tutorialToAssign && (
        <TutorialAssignmentModal
          tutorialId={tutorialToAssign.id}
          tutorialTitle={tutorialToAssign.title}
          isOpen={showAssignmentModal}
          onClose={() => {
            setShowAssignmentModal(false);
            setTutorialToAssign(null);
          }}
          onAssign={handleAssignmentComplete}
        />
      )}
    </div>
  );
};

export default CourseCatalog;
